// CADViewer - 3D CAD model viewer using React Three Fiber
// Follows Twin Engine integration pattern for JustSell
import { Suspense, useRef, useState, useEffect, useCallback } from 'react'
import { Canvas, ThreeEvent, useThree } from '@react-three/fiber'
import {
  OrbitControls,
  Environment,
  useGLTF,
  Html,
  PresentationControls,
  Stage,
  ContactShadows,
} from '@react-three/drei'
import * as THREE from 'three'
import { useTranslation } from '../hooks/useTranslation'
import type { TranslationKey } from '../../locales/types'
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  CubeTransparentIcon,
} from '@heroicons/react/24/outline'

// =============================================================================
// Types
// =============================================================================

export interface CADPart {
  name: string
  triangles?: number
  color?: [number, number, number]
}

export interface CADViewerProps {
  /** Twin project ID to load (use this OR sampleId) */
  projectId?: string
  /** Sample model ID to load (e.g., 'sv06-extruder') */
  sampleId?: string
  /** Callback when a part is selected */
  onPartSelect?: (partName: string | null, partData?: CADPart) => void
  /** Callback when a part is hovered */
  onPartHover?: (partName: string | null) => void
  /** Optional: externally controlled selected part */
  selectedPart?: string | null
  /** Optional: externally controlled highlighted parts (e.g., from BOM) */
  highlightedParts?: string[]
  /** Optional: custom highlight color */
  highlightColor?: string
  /** Optional: custom selection color */
  selectionColor?: string
  /** Optional: show part labels on hover */
  showLabels?: boolean
  /** Optional: enable shadows */
  shadows?: boolean
  /** Optional: background color */
  backgroundColor?: string
  /** Optional: className for container */
  className?: string
  /** Optional: style for container */
  style?: React.CSSProperties
}

// =============================================================================
// Model Component (loads and renders the GLB)
// =============================================================================

interface ModelProps {
  url: string
  selectedPart: string | null
  highlightedParts: string[]
  highlightColor: THREE.Color
  selectionColor: THREE.Color
  onPartSelect: (name: string | null) => void
  onPartHover: (name: string | null) => void
  showLabels: boolean
  onBoundsCalculated?: (bounds: { size: THREE.Vector3; bottomY: number }) => void
}

function Model({
  url,
  selectedPart,
  highlightedParts,
  highlightColor,
  selectionColor,
  onPartSelect,
  onPartHover,
  showLabels,
  onBoundsCalculated,
}: ModelProps) {
  const { scene } = useGLTF(url)
  const { camera } = useThree()
  const [hoveredPart, setHoveredPart] = useState<string | null>(null)
  const originalMaterials = useRef<Map<string, THREE.Material | THREE.Material[]>>(new Map())
  const meshRefs = useRef<Map<string, THREE.Mesh>>(new Map())
  const groupRef = useRef<THREE.Group>(null)

  // Center the model and adjust camera based on bounding box
  useEffect(() => {
    if (!scene) return

    // Compute bounding box
    const box = new THREE.Box3().setFromObject(scene)
    const center = box.getCenter(new THREE.Vector3())
    const size = box.getSize(new THREE.Vector3())

    // Offset scene to center it
    scene.position.sub(center)

    // Calculate optimal camera distance based on model size
    const maxDim = Math.max(size.x, size.y, size.z)
    const fov = (camera as THREE.PerspectiveCamera).fov * (Math.PI / 180)
    const cameraDistance = maxDim / (2 * Math.tan(fov / 2)) * 1.5 // 1.5x padding

    // Position camera to fit model
    camera.position.set(0, 0, cameraDistance)
    camera.lookAt(0, 0, 0)
    camera.updateProjectionMatrix()

    // Report bounds for shadow positioning
    if (onBoundsCalculated) {
      const bottomY = -size.y / 2 // Model is centered, so bottom is at -height/2
      onBoundsCalculated({ size, bottomY })
    }
  }, [scene, camera, onBoundsCalculated])

  // Store original materials on first render
  useEffect(() => {
    scene.traverse((child) => {
      if (child instanceof THREE.Mesh && child.name) {
        originalMaterials.current.set(child.name, child.material)
        meshRefs.current.set(child.name, child)
      }
    })
  }, [scene])

  // Apply highlighting and selection
  useEffect(() => {
    meshRefs.current.forEach((mesh, name) => {
      const original = originalMaterials.current.get(name)
      if (!original) return

      const isSelected = name === selectedPart
      const isHighlighted = highlightedParts.includes(name)
      const isHovered = name === hoveredPart

      if (isSelected) {
        // Selection: bright color with emissive
        const mat = new THREE.MeshStandardMaterial({
          color: selectionColor,
          emissive: selectionColor,
          emissiveIntensity: 0.3,
          metalness: 0.5,
          roughness: 0.5,
        })
        mesh.material = mat
      } else if (isHighlighted) {
        // Highlight: tinted color
        const mat = new THREE.MeshStandardMaterial({
          color: highlightColor,
          emissive: highlightColor,
          emissiveIntensity: 0.1,
          metalness: 0.3,
          roughness: 0.6,
        })
        mesh.material = mat
      } else if (isHovered) {
        // Hover: slight glow
        const baseMat = Array.isArray(original) ? original[0] : original
        if (baseMat instanceof THREE.MeshStandardMaterial) {
          const mat = baseMat.clone()
          mat.emissive = new THREE.Color(0x444444)
          mat.emissiveIntensity = 0.2
          mesh.material = mat
        }
      } else {
        // Restore original
        mesh.material = original
      }
    })
  }, [selectedPart, highlightedParts, hoveredPart, highlightColor, selectionColor])

  const handlePointerOver = useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      e.stopPropagation()
      const mesh = e.object as THREE.Mesh
      const name = mesh.name
      if (name) {
        setHoveredPart(name)
        onPartHover(name)
        document.body.style.cursor = 'pointer'
      }
    },
    [onPartHover]
  )

  const handlePointerOut = useCallback(() => {
    setHoveredPart(null)
    onPartHover(null)
    document.body.style.cursor = 'auto'
  }, [onPartHover])

  const handleClick = useCallback(
    (e: ThreeEvent<MouseEvent>) => {
      e.stopPropagation()
      const mesh = e.object as THREE.Mesh
      const name = mesh.name
      onPartSelect(name || null)
    },
    [onPartSelect]
  )

  return (
    <group>
      <primitive
        object={scene}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
        onClick={handleClick}
      />
      {showLabels && hoveredPart && (
        <Html position={[0, 2, 0]} center>
          <div
            style={{
              background: 'rgba(0,0,0,0.8)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}
          >
            {hoveredPart}
          </div>
        </Html>
      )}
    </group>
  )
}

// =============================================================================
// Loading Component
// =============================================================================

function Loader() {
  const { t } = useTranslation()
  return (
    <Html center>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <div
          style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e5e7eb',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
        <span style={{ color: '#6b7280', fontSize: '14px' }}>{t('cad.viewer.loadingViewer')}</span>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </Html>
  )
}

// =============================================================================
// Main Viewer Component
// =============================================================================

export function CADViewer({
  projectId,
  sampleId,
  onPartSelect,
  onPartHover,
  selectedPart: externalSelectedPart,
  highlightedParts = [],
  highlightColor = '#22c55e',
  selectionColor = '#3b82f6',
  showLabels = true,
  shadows = true,
  backgroundColor = '#f5f5f5',
  className,
  style,
}: CADViewerProps) {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const [modelUrl, setModelUrl] = useState<string | null>(null)
  const [internalSelectedPart, setInternalSelectedPart] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [modelBounds, setModelBounds] = useState<{ size: THREE.Vector3; bottomY: number } | null>(null)

  // Use external selection if provided, otherwise use internal state
  const selectedPart = externalSelectedPart !== undefined ? externalSelectedPart : internalSelectedPart

  // Fetch model URL from Twin API
  useEffect(() => {
    // Sample model - use direct URL
    if (sampleId) {
      console.log('[CADViewer] Loading sample model:', sampleId)
      setModelUrl(`/api/twin/samples/${sampleId}/model.glb`)
      setLoading(false)
      return
    }

    // Project model - fetch URL from API
    if (!projectId) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    fetch(`/api/twin/projects/${projectId}/model`)
      .then((res) => {
        if (!res.ok) {
          if (res.status === 404) {
            throw new Error('No converted model available')
          }
          throw new Error(`Failed to load model: ${res.status}`)
        }
        return res.json()
      })
      .then((data) => {
        console.log('[CADViewer] Model URL:', data.url)
        setModelUrl(data.url)
        setLoading(false)
      })
      .catch((err) => {
        console.error('[CADViewer] Error loading model:', err)
        setError(err.message)
        setLoading(false)
      })
  }, [projectId, sampleId])

  const handlePartSelect = useCallback(
    (name: string | null) => {
      setInternalSelectedPart(name)
      if (onPartSelect) {
        onPartSelect(name, name ? { name } : undefined)
      }
    },
    [onPartSelect]
  )

  const handlePartHover = useCallback(
    (name: string | null) => {
      if (onPartHover) {
        onPartHover(name)
      }
    },
    [onPartHover]
  )

  // Click on background to deselect
  const handleBackgroundClick = useCallback(() => {
    handlePartSelect(null)
  }, [handlePartSelect])

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true))
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false))
    }
  }, [])

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  // Error state
  if (error) {
    return (
      <div
        ref={containerRef}
        className={className}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f3f4f6',
                    borderRadius: '8px',
          ...style,
        }}
      >
        <ExclamationTriangleIcon className="h-12 w-12 text-red-400" />
        <p className="mt-4 text-sm text-gray-700 font-medium">{t('cad.viewer.loadFailed')}</p>
        <p className="mt-1 text-xs text-gray-500">{error}</p>
      </div>
    )
  }

  // Loading state (before model URL is fetched)
  if (loading) {
    return (
      <div
        ref={containerRef}
        className={className}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: backgroundColor,
                    borderRadius: '8px',
          ...style,
        }}
      >
        <ArrowPathIcon className="h-10 w-10 text-gray-400 animate-spin" />
        <p className="mt-4 text-sm text-gray-400">{t('cad.viewer.loading')}</p>
      </div>
    )
  }

  // No model state
  if (!modelUrl) {
    return (
      <div
        ref={containerRef}
        className={className}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f3f4f6',
                    borderRadius: '8px',
          ...style,
        }}
      >
        <CubeTransparentIcon className="h-16 w-16 text-gray-300" />
        <p className="mt-4 text-sm text-gray-500">{t('cad.viewer.noModel')}</p>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        background: backgroundColor,
        borderRadius: '8px',
        position: 'relative',
        overflow: 'hidden',
        ...style,
      }}
    >
      {/* Toolbar */}
      <div style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 10, display: 'flex', gap: '8px' }}>
        <button
          onClick={toggleFullscreen}
          className="p-2 bg-white/90 rounded-lg shadow-sm hover:bg-white transition-colors"
          title={isFullscreen ? t('cad.viewer.exitFullscreen') : t('cad.viewer.fullscreen')}
        >
          {isFullscreen ? (
            <ArrowsPointingInIcon className="h-5 w-5 text-gray-600" />
          ) : (
            <ArrowsPointingOutIcon className="h-5 w-5 text-gray-600" />
          )}
        </button>
      </div>

      {/* 3D Canvas */}
      <Canvas
        shadows={shadows}
        dpr={[1, 2]}
        camera={{ position: [0, 0, 150], fov: 45, near: 0.1, far: 50000 }}
        onPointerMissed={handleBackgroundClick}
        style={{ borderRadius: '8px', background: backgroundColor }}
      >
        <ambientLight intensity={0.5} />
        <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} intensity={1} />
        <pointLight position={[-10, -10, -10]} intensity={0.5} />

        <Suspense fallback={<Loader />}>
          <group position={[0, 0, 0]}>
            <Model
              url={modelUrl}
              selectedPart={selectedPart}
              highlightedParts={highlightedParts}
              highlightColor={new THREE.Color(highlightColor)}
              selectionColor={new THREE.Color(selectionColor)}
              onPartSelect={handlePartSelect}
              onPartHover={handlePartHover}
              showLabels={showLabels}
              onBoundsCalculated={setModelBounds}
            />
          </group>
        </Suspense>

        {/* Ground shadow */}
        {shadows && modelBounds && (
          <ContactShadows
            position={[0, modelBounds.bottomY, 0]}
            opacity={0.4}
            scale={Math.max(modelBounds.size.x, modelBounds.size.z) * 2}
            blur={2}
            far={modelBounds.size.y * 2}
          />
        )}

        <OrbitControls
          makeDefault
          enablePan={true}
          enableZoom={true}
          enableRotate={true}
          minDistance={1}
          maxDistance={10000}
        />

        <Environment preset="studio" />
      </Canvas>
    </div>
  )
}

// Status badge component
export function CADStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation()
  const config: Record<string, { labelKey: string; className: string }> = {
    draft: { labelKey: 'cad.status.draft', className: 'bg-blue-100 text-blue-600' },
    pending: { labelKey: 'cad.status.pending', className: 'bg-gray-100 text-gray-600' },
    processing: { labelKey: 'cad.status.processing', className: 'bg-yellow-100 text-yellow-700' },
    ready: { labelKey: 'cad.status.ready', className: 'bg-green-100 text-green-700' },
    failed: { labelKey: 'cad.status.failed', className: 'bg-red-100 text-red-700' },
  }

  const { labelKey, className } = config[status] || config.pending

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${className}`}>
      {t(labelKey as TranslationKey)}
    </span>
  )
}

export default CADViewer
